import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import type { StoreIndex } from "../data/store";
import { LANGUAGES } from "../lib/format";
import { LangLogo } from "./LangLogo";

const prefersReducedMotion = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const CATEGORIES = [
    { key: "patterns", label: "Patterns", desc: "Byte signatures, generated per language" },
    { key: "declarations", label: "Declarations", desc: "IDA-style C function signatures" },
    { key: "structs", label: "Structs", desc: "Reconstructed schema headers" },
] as const;

type Card = { key: string; label: string; language: string; meta?: string };

/**
 * The "PATTERNS" overlay. Pattern cards come from the static `LANGUAGES` list
 * (the frontend generates each); struct cards come from the store. Picking a
 * card hands `(category, key)` up to App, which puts it in the URL (nuqs).
 */
export function PatternChooser({
    open,
    onClose,
    onPick,
    store,
}: {
    open: boolean;
    onClose: () => void;
    onPick: (category: string, key: string) => void;
    store: StoreIndex | null;
}) {
    const dialogRef = useRef<HTMLDialogElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const [activeCat, setActiveCat] = useState<string>("patterns");

    useEffect(() => {
        const dlg = dialogRef.current;
        if (!dlg) return;
        if (open && !dlg.open) {
            dlg.showModal();
            if (!prefersReducedMotion() && panelRef.current) {
                gsap.from(panelRef.current, { opacity: 0, y: 22, scale: 0.96, duration: 0.42, ease: "power3.out" });
            }
        } else if (!open && dlg.open) {
            dlg.close();
        }
    }, [open]);

    useEffect(() => {
        if (!open || prefersReducedMotion() || !panelRef.current) return;
        gsap.fromTo(
            panelRef.current.querySelectorAll(".chooser-card"),
            { opacity: 0, scale: 0.6 },
            { opacity: 1, scale: 1, stagger: 0.05, duration: 0.26, ease: "back.out(2.2)" },
        );
    }, [open, activeCat, store]);

    const handleBackdrop = (e: React.MouseEvent<HTMLDialogElement>) => {
        if (e.target === dialogRef.current) onClose();
    };

    const category = CATEGORIES.find((c) => c.key === activeCat);

    const cards: Card[] =
        activeCat === "patterns"
            ? LANGUAGES.map((l) => ({ key: l.key, label: l.label, language: l.language }))
            : activeCat === "declarations"
              ? [{ key: "all", label: "Declarations", language: "cpp", meta: "C signatures" }]
              : (store?.structs ?? []).map((s) => ({
                    key: s.name,
                    label: s.name,
                    language: "cpp",
                    meta: `updated ${s.updated}`,
                }));

    return (
        <dialog ref={dialogRef} className="modal chooser" onClick={handleBackdrop} onClose={onClose} onCancel={onClose}>
            {open && (
                <div ref={panelRef} className="modal-panel chooser-panel">
                    <button type="button" className="modal-close" aria-label="Close" onClick={onClose}>
                        ✕
                    </button>

                    <h2 className="chooser-title">Select a dump</h2>

                    <div className="chooser-tabs" role="tablist" aria-label="Category">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.key}
                                type="button"
                                role="tab"
                                aria-selected={c.key === activeCat}
                                className={`chooser-tab ${c.key === activeCat ? "is-active" : ""}`}
                                onClick={() => setActiveCat(c.key)}
                            >
                                {c.label}
                            </button>
                        ))}
                    </div>

                    {category?.desc && <p className="chooser-desc">{category.desc}</p>}

                    <div className="chooser-grid">
                        {cards.map((c) => (
                            <button
                                key={c.key}
                                type="button"
                                className="chooser-card"
                                onClick={() => onPick(activeCat, c.key)}
                            >
                                <LangLogo language={c.language} />
                                <span className="chooser-card-label">{c.label}</span>
                                {c.meta && <span className="chooser-card-meta">{c.meta}</span>}
                            </button>
                        ))}
                        {cards.length === 0 && <p className="chooser-empty">nothing here yet</p>}
                    </div>
                </div>
            )}
        </dialog>
    );
}
